import { useState, useEffect, useRef } from 'react'
import { Receipt, Plus, Trash2, Pencil, FileText, Printer, X, Calendar, ChevronLeft, ChevronRight, ArrowDownAZ, CalendarClock } from 'lucide-react'
import toast from 'react-hot-toast'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import leiloesService from '../../services/leiloesService'
import recibosService from '../../services/recibosService'
import NovoReciboModal from './NovoReciboModal'
import { downloadPdf } from '../../utils/pdfUtils'

// ─── Date Range Picker ────────────────────────────────────────────────────────
const PRESETS = [
  { label: 'Hoje', getValue: () => { const d = new Date(); const s = d.toISOString().slice(0,10); return [s, s] } },
  { label: 'Ontem', getValue: () => { const d = new Date(); d.setDate(d.getDate()-1); const s = d.toISOString().slice(0,10); return [s, s] } },
  { label: 'Últimos 7 dias', getValue: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate()-6); return [s.toISOString().slice(0,10), e.toISOString().slice(0,10)] } },
  { label: 'Últimos 30 dias', getValue: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate()-29); return [s.toISOString().slice(0,10), e.toISOString().slice(0,10)] } },
  { label: 'Este mês', getValue: () => { const d = new Date(); const s = new Date(d.getFullYear(), d.getMonth(), 1); return [s.toISOString().slice(0,10), d.toISOString().slice(0,10)] } },
  { label: 'Mês passado', getValue: () => { const d = new Date(); const s = new Date(d.getFullYear(), d.getMonth()-1, 1); const e = new Date(d.getFullYear(), d.getMonth(), 0); return [s.toISOString().slice(0,10), e.toISOString().slice(0,10)] } },
]

function DateRangePicker({ value, onChange, onClose }) {
  const [startDate, setStartDate] = useState(value?.[0] || '')
  const [endDate, setEndDate] = useState(value?.[1] || '')
  const [month1, setMonth1] = useState(() => { const d = value?.[0] ? new Date(value[0]+'T12:00:00') : new Date(); return { year: d.getFullYear(), month: d.getMonth() } })
  const month2 = { year: month1.month === 11 ? month1.year + 1 : month1.year, month: (month1.month + 1) % 12 }

  const prevMonth = () => setMonth1(p => p.month === 0 ? { year: p.year - 1, month: 11 } : { year: p.year, month: p.month - 1 })
  const nextMonth = () => setMonth1(p => p.month === 11 ? { year: p.year + 1, month: 0 } : { year: p.year, month: p.month + 1 })

  const MONTHS = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']
  const DAYS = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb']

  const getDays = (year, month) => {
    const first = new Date(year, month, 1).getDay()
    const total = new Date(year, month + 1, 0).getDate()
    const days = []
    for (let i = 0; i < first; i++) days.push(null)
    for (let i = 1; i <= total; i++) days.push(i)
    return days
  }

  const handleDayClick = (year, month, day) => {
    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`
    if (!startDate || (startDate && endDate)) {
      setStartDate(dateStr)
      setEndDate('')
    } else {
      if (dateStr < startDate) { setEndDate(startDate); setStartDate(dateStr) }
      else setEndDate(dateStr)
    }
  }

  const isInRange = (year, month, day) => {
    if (!startDate || !endDate) return false
    const d = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`
    return d >= startDate && d <= endDate
  }
  const isStart = (year, month, day) => `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}` === startDate
  const isEnd = (year, month, day) => `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}` === endDate

  const applyPreset = (preset) => {
    const [s, e] = preset.getValue()
    setStartDate(s); setEndDate(e)
  }

  const handleApply = () => { onChange([startDate, endDate]); onClose() }
  const fmtDisplay = (d) => d ? d.split('-').reverse().join('/') : ''

  const renderMonth = ({ year, month }) => (
    <div>
      <div className="text-center font-semibold text-sm text-gray-700 mb-2">{MONTHS[month]} {year}</div>
      <div className="grid grid-cols-7 gap-0 text-center text-xs">
        {DAYS.map(d => <div key={d} className="py-1 font-medium text-gray-500">{d}</div>)}
        {getDays(year, month).map((day, i) => day ? (
          <div key={i} onClick={() => handleDayClick(year, month, day)}
            className={`py-1.5 cursor-pointer rounded text-xs transition-colors ${
              isStart(year, month, day) || isEnd(year, month, day) ? 'bg-blue-600 text-white font-bold' :
              isInRange(year, month, day) ? 'bg-blue-100 text-blue-800' :
              'hover:bg-gray-100 text-gray-700'
            }`}>{day}</div>
        ) : <div key={i} />)}
      </div>
    </div>
  )

  return (
    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-3 sm:p-4"
      style={{ width: 'min(calc(100vw - 1rem), 580px)' }}>
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        {/* Presets — scroll horizontal no mobile, vertical no sm+ */}
        <div className="flex sm:flex-col gap-1 overflow-x-auto sm:overflow-visible sm:border-r sm:border-gray-200 sm:pr-4 sm:min-w-[130px] pb-1 sm:pb-0 flex-shrink-0">
          {PRESETS.map(p => (
            <button key={p.label} onClick={() => applyPreset(p)}
              className="whitespace-nowrap text-left text-sm px-3 py-1.5 rounded hover:bg-blue-50 hover:text-blue-700 text-gray-700 transition-colors flex-shrink-0">
              {p.label}
            </button>
          ))}
        </div>
        {/* Calendars */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-3">
            <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded"><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex-1" />
            <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {renderMonth(month1)}
            <div className="hidden sm:block">{renderMonth(month2)}</div>
          </div>
        </div>
      </div>
      {/* Footer */}
      <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 mt-4 pt-3 border-t border-gray-200">
        <span className="text-sm text-gray-600 mr-auto">{fmtDisplay(startDate)} — {fmtDisplay(endDate)}</span>
        <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
        <button onClick={handleApply} disabled={!startDate || !endDate}
          className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
          Aplicar
        </button>
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function RecibosPage() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [recibos, setRecibos] = useState([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [reciboEdit, setReciboEdit] = useState(null)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [pdfTitle, setPdfTitle] = useState('')
  const [dateRange, setDateRange] = useState([null, null])
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [tipoFiltro, setTipoFiltro] = useState('')
  const [ordenacao, setOrdenacao] = useState('data') // 'data' | 'alfabetica'
  const iframeRef = useRef(null)
  const datePickerRef = useRef(null)

  // Fecha date picker ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) setShowDatePicker(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Carrega leilões
  useEffect(() => {
    if (!orgId) return
    leiloesService.getByOrganizador(orgId, { status: 'on' }).then(list => {
      const sorted = [...list].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
      setLeiloes(sorted)
      if (sorted.length > 0) setLeilaoId(String(sorted[0].id))
    })
  }, [orgId])

  // Carrega recibos quando leilão ou período muda (auto-pesquisa)
  useEffect(() => {
    if (!orgId) return
    fetchRecibos()
  }, [orgId, leilaoId, dateRange[0], dateRange[1], tipoFiltro, ordenacao]) // eslint-disable-line

  const fetchRecibos = async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const data = await recibosService.list(orgId, leilaoId || null, {
        dataInicio: dateRange[0] || undefined,
        dataFim: dateRange[1] || undefined,
        tipo: tipoFiltro || undefined,
        ordenar: ordenacao,
      })
      setRecibos(data)
    } catch (err) {
      console.error('Erro ao carregar recibos:', err)
    } finally {
      setLoading(false)
    }
  }

  const [deleteId, setDeleteId] = useState(null)

  const handleDelete = async (id) => { setDeleteId(id) }

  const confirmDelete = async () => {
    if (!deleteId) return
    try {
      await recibosService.delete(orgId, deleteId)
      toast.success('Recibo excluído')
      fetchRecibos()
    } catch (err) {
      toast.error('Erro ao excluir recibo')
    } finally {
      setDeleteId(null)
    }
  }

  const handlePrint = async (id) => {
    try {
      const blob = await recibosService.getPdf(orgId, id)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      setPdfUrl(url)
      const recibo = recibos.find(r => r.id === id)
      setPdfTitle(`Recibo Nº ${recibo?.numero || id} - ${recibo?.cliente_nome || ''}`)
      setShowPdfModal(true)
    } catch (err) {
      toast.error('Erro ao gerar PDF do recibo')
    }
  }

  const handlePrintRelacao = async () => {
    try {
      const blob = await recibosService.getRelacaoPdf(orgId, leilaoId || null, ordenacao)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      setPdfUrl(url)
      setPdfTitle('Relação de Recibos')
      setShowPdfModal(true)
    } catch (err) {
      toast.error('Erro ao gerar relação de recibos')
    }
  }

  const closePdfModal = () => { setShowPdfModal(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  const formatDateTime = (iso) => {
    if (!iso) return '-'
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const parts = dateStr.split('-')
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`
    return dateStr
  }

  const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0)

  // Totais
  const totalRecebimentos = recibos.filter(r => r.tipo === 'recebimento').reduce((s, r) => s + (r.valor || 0), 0)
  const totalPagamentos = recibos.filter(r => r.tipo === 'pagamento').reduce((s, r) => s + (r.valor || 0), 0)
  const totalGeral = recibos.reduce((s, r) => s + (r.valor || 0), 0)

  const dateRangeLabel = dateRange[0] && dateRange[1]
    ? `${formatDate(dateRange[0])} - ${formatDate(dateRange[1])}`
    : 'Todos os períodos'

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para gerenciar recibos.
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Receipt className="w-5 h-5 text-emerald-600" />
        <h1 className="text-lg font-semibold text-gray-800">Recibos</h1>
      </div>

      {/* Filtros + Ações */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1 flex-[2] min-w-[220px]">
            <label className="text-xs text-gray-500 font-medium">Leilão</label>
            <select
              value={leilaoId}
              onChange={e => setLeilaoId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos leilões ativos</option>
              {leiloes.map(l => (
                <option key={l.id} value={String(l.id)}>{l.nome}</option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="flex flex-col gap-1 relative" ref={datePickerRef}>
            <label className="text-xs text-gray-500 font-medium">Período</label>
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white hover:border-blue-400 transition-colors min-w-[200px]"
            >
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className={dateRange[0] ? 'text-gray-800' : 'text-gray-400'}>{dateRangeLabel}</span>
            </button>
            {showDatePicker && (
              <DateRangePicker
                value={dateRange}
                onChange={(range) => { setDateRange(range); setShowDatePicker(false) }}
                onClose={() => setShowDatePicker(false)}
              />
            )}
          </div>

          {/* Tipo */}
          <div className="flex flex-col gap-1 min-w-[140px]">
            <label className="text-xs text-gray-500 font-medium">Tipo</label>
            <select
              value={tipoFiltro}
              onChange={e => setTipoFiltro(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="recebimento">Recebimento</option>
              <option value="pagamento">Pagamento</option>
            </select>
          </div>

          {/* Ordenação */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Ordenar por</label>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden h-[38px]">
              <button
                onClick={() => setOrdenacao('data')}
                title="Ordenar por data"
                className={`flex items-center gap-1.5 px-3 text-sm font-medium transition-colors ${
                  ordenacao === 'data' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <CalendarClock className="w-3.5 h-3.5" /> Data
              </button>
              <button
                onClick={() => setOrdenacao('alfabetica')}
                title="Ordenar por nome (A-Z)"
                className={`flex items-center gap-1.5 px-3 text-sm font-medium border-l border-gray-300 transition-colors ${
                  ordenacao === 'alfabetica' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <ArrowDownAZ className="w-3.5 h-3.5" /> A-Z
              </button>
            </div>
          </div>

          {dateRange[0] && (
            <button
              onClick={() => setDateRange([null, null])}
              className="px-3 py-2 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors h-[38px]"
              title="Limpar período"
            >
              ✕ Limpar
            </button>
          )}

          <div className="flex-1" />

          <button
            onClick={handlePrintRelacao}
            disabled={recibos.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 h-[38px]"
          >
            <Printer className="w-4 h-4" />
            Imprimir Lista
          </button>

          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-lg text-sm font-medium hover:bg-cyan-600 transition-colors h-[38px]"
          >
            <Plus className="w-4 h-4" />
            Novo Recibo
          </button>
        </div>
      </div>

      {/* Lista de recibos */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-8 text-gray-400 text-sm">Carregando...</div>
        ) : recibos.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">Nenhum recibo encontrado.</div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-gray-100">
              {recibos.map(r => (
                <div key={r.id} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 truncate">{r.cliente_nome || '—'}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {r.numero ? `Nº ${r.numero} · ` : ''}{formatDate(r.data)} · gerado em {formatDateTime(r.created_at)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => handlePrint(r.id)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
                        <FileText className="w-4 h-4" />
                      </button>
                      <button onClick={() => setReciboEdit(r)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(r.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      r.tipo === 'recebimento' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {r.tipo === 'recebimento' ? 'Recebimento' : 'Pagamento'}
                    </span>
                    <span className="font-bold text-gray-900 ml-auto">{formatCurrency(r.valor)}</span>
                  </div>
                </div>
              ))}
              {/* Totais mobile */}
              <div className="px-4 py-3 bg-gray-50 border-t-2 border-gray-300 space-y-1">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{recibos.length} recibo{recibos.length !== 1 ? 's' : ''}</span>
                  <span className="font-bold text-gray-900 text-sm">{formatCurrency(totalGeral)}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 text-xs text-gray-500">
                  <span>Receb.: <span className="font-semibold text-green-700">{formatCurrency(totalRecebimentos)}</span></span>
                  {totalPagamentos > 0 && <span>Pag.: <span className="font-semibold text-orange-700">{formatCurrency(totalPagamentos)}</span></span>}
                </div>
              </div>
            </div>
            {/* Desktop table */}
            <table className="hidden sm:table w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Nº</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Data</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Gerado em</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Cliente</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Tipo</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Valor</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {recibos.map(r => (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-700">{r.numero || '-'}</td>
                    <td className="px-4 py-3">{formatDate(r.data)}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                    <td className="px-4 py-3">{r.cliente_nome || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        r.tipo === 'recebimento' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {r.tipo === 'recebimento' ? 'Recebimento' : 'Pagamento'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(r.valor)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handlePrint(r.id)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Imprimir recibo">
                          <FileText className="w-4 h-4" />
                        </button>
                        <button onClick={() => setReciboEdit(r)}
                          className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Editar recibo">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(r.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Excluir recibo">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr>
                  <td className="px-4 py-3 font-semibold text-gray-700" colSpan={4}>
                    {recibos.length} recibo{recibos.length !== 1 ? 's' : ''}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-gray-500">
                    <div>Recebimentos: <span className="font-semibold text-green-700">{formatCurrency(totalRecebimentos)}</span></div>
                    {totalPagamentos > 0 && <div>Pagamentos: <span className="font-semibold text-orange-700">{formatCurrency(totalPagamentos)}</span></div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-bold text-gray-900 text-base">{formatCurrency(totalGeral)}</span>
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </>
        )}
      </div>

      {/* Modal Novo Recibo */}
      {showModal && (
        <NovoReciboModal
          orgId={orgId}
          leiloes={leiloes}
          leilaoIdInicial={leilaoId}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); toast.success('Recibo criado com sucesso'); fetchRecibos() }}
        />
      )}

      {/* Modal Editar Recibo */}
      {reciboEdit && (
        <NovoReciboModal
          orgId={orgId}
          leiloes={leiloes}
          recibo={reciboEdit}
          onClose={() => setReciboEdit(null)}
          onSaved={() => { setReciboEdit(null); toast.success('Recibo atualizado'); fetchRecibos() }}
        />
      )}

      {/* Modal PDF */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">{pdfTitle}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(pdfUrl, `${pdfTitle}.pdf`)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  Salvar PDF
                </button>
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
                <button onClick={closePdfModal} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title={pdfTitle} style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}

      {/* Confirmação de exclusão */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Excluir recibo</h3>
            <p className="text-sm text-gray-600 mb-4">Deseja realmente excluir este recibo? Esta ação não pode ser desfeita.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                Cancelar
              </button>
              <button onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
